import { TToken, TUser } from './../../models/index'
import { IAuthState } from './types'
import {
  signUpThunk,
  signInThunk,
  refreshTokenThunk,
  forgotPasswordThunk,
  checkTokenToUpdatePasswordThunk,
  updatePasswordThunk,
  logOutThunk,
  resendInvitationThunk,
  uploadsProfilePictureThunk,
  checkEmailValidationThunk,
  getMeThunk,
  getNearbySuburbsThunk,
  getAddressSuggestionsThunk,
  getJobDetailsBasedOnAddressThunk
} from './thunks'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { TPosition } from '../../models/Onboarding/onboarding'

const initialState: IAuthState = {
  inProgress: false,
  createAccinProgress: false,
  user: null,
  profilePicLink: '',
  addressSuggestions: [],
  nearbySuburbs: [],
  showSignUpDetails: false,
  getAddressPosition: null,
  showNotVerifiedError: false,
  submitStage: false, // Added it to know the candidate submit the stage 1 from onboarding(Qualification mandatory docs validation)
  error: ''
}

const usersSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setUserAction: (state, action: PayloadAction<TUser>) => {
      state.user = action.payload;
    },
    appLogOutAction: (state) => {
      state.user = null;
      localStorage.removeItem("token");
    },
    setProfilePictureAction: (state, action: PayloadAction<string>) => {
      if (state.user) {
        state.user = {
          ...state.user,
          photo: action.payload,
        };
      }
    },
    setTokenAction: (state, action: PayloadAction<TToken>) => {
      if (action.payload) {
        localStorage.removeItem("token");
        const tokenData = {
          token: action.payload,
        };
        localStorage.setItem("token", JSON.stringify(tokenData));
      }
    },
    resetAuthErrorAction: (state) => {
      state.error = null;
    },
    setShowSignUpDetails: (state, action: PayloadAction<boolean>) => {
      state.showSignUpDetails = action.payload;
    },
    setPositionAction: (state, action: PayloadAction<TPosition>) => {
      state.getAddressPosition = action.payload;
    },
    resetAddressSuggestionsListAction: (state) => {
      state.addressSuggestions = [];
    },
    setShowNotVerifiedErrorAction: (state, action: PayloadAction<boolean>) => {
      state.showNotVerifiedError = action.payload;
    },
    setSubmitStageAction: (state, action: PayloadAction<boolean>) => {
      state.submitStage = action.payload;
    },
  },
  // Async actions
  extraReducers: (builder) => {
    // signUp
    builder.addCase(signUpThunk.pending, (state, action) => {
      state.inProgress = true;
      state.error = null;
    });
    builder.addCase(signUpThunk.fulfilled, (state, action) => {
      state.inProgress = false;

      const signupEmail = sessionStorage.getItem("signup_email");

      // Clear all session storage
      sessionStorage.clear();

      // Restore the signup_email value
      if (signupEmail !== null) {
        sessionStorage.setItem("signup_email", signupEmail);
      }
    });
    builder.addCase(signUpThunk.rejected, (state, action) => {
      state.inProgress = false;
      state.error = action.payload;
    });

    // email Validate
    builder.addCase(checkEmailValidationThunk.pending, (state, action) => {
      state.createAccinProgress = true;
      state.error = null;
    });
    builder.addCase(checkEmailValidationThunk.fulfilled, (state, action) => {
      state.createAccinProgress = false;
    });
    builder.addCase(checkEmailValidationThunk.rejected, (state, action) => {
      state.createAccinProgress = false;
      state.error = action.payload;
    });

    // signIn
    builder.addCase(signInThunk.pending, (state, action) => {
      state.inProgress = true;
      state.error = null;
    });
    builder.addCase(signInThunk.fulfilled, (state, action) => {
      state.inProgress = false;
      sessionStorage.clear();

      const userDetails = action.payload.data.user;
      state.user = userDetails;
    });
    builder.addCase(signInThunk.rejected, (state, action) => {
      state.inProgress = false;
      state.error = action.payload;
    });

    // refreshToken
    builder.addCase(refreshTokenThunk.pending, (state, action) => {
      state.inProgress = true;
      state.error = null;
    });
    builder.addCase(refreshTokenThunk.fulfilled, (state, action) => {
      state.inProgress = false;
    });
    builder.addCase(refreshTokenThunk.rejected, (state, action) => {
      state.inProgress = false;
      state.error = action.payload;
    });

    // forgotPassword
    builder.addCase(forgotPasswordThunk.pending, (state, action) => {
      state.inProgress = true;
      state.error = null;
    });
    builder.addCase(forgotPasswordThunk.fulfilled, (state, action) => {
      state.inProgress = false;
    });
    builder.addCase(forgotPasswordThunk.rejected, (state, action) => {
      state.inProgress = false;
      state.error = action.payload;
    });

    // Check reset pass token
    builder.addCase(
      checkTokenToUpdatePasswordThunk.pending,
      (state, action) => {
        state.inProgress = true;
        state.error = null;
      }
    );
    builder.addCase(
      checkTokenToUpdatePasswordThunk.fulfilled,
      (state, action) => {
        state.inProgress = false;
      }
    );
    builder.addCase(
      checkTokenToUpdatePasswordThunk.rejected,
      (state, action) => {
        state.inProgress = false;
        state.error = action.payload;
      }
    );

    // Check reset pass token
    builder.addCase(updatePasswordThunk.pending, (state, action) => {
      state.inProgress = true;
      state.error = null;
    });
    builder.addCase(updatePasswordThunk.fulfilled, (state, action) => {
      state.inProgress = false;
    });
    builder.addCase(updatePasswordThunk.rejected, (state, action) => {
      state.inProgress = false;
      state.error = action.payload;
    });

    // logOut
    builder.addCase(logOutThunk.pending, (state, action) => {
      state.inProgress = true;
      state.error = null;
    });
    builder.addCase(logOutThunk.fulfilled, (state, action) => {
      state.inProgress = false;

      sessionStorage.clear();
      localStorage.clear();
    });
    builder.addCase(logOutThunk.rejected, (state, action) => {
      state.inProgress = false;
      state.error = action.payload;

      sessionStorage.clear();
      localStorage.clear();
    });

    // Resend invitation
    builder.addCase(resendInvitationThunk.pending, (state, action) => {
      state.inProgress = true;
      state.error = null;
    });
    builder.addCase(resendInvitationThunk.fulfilled, (state, action) => {
      state.inProgress = false;
    });
    builder.addCase(resendInvitationThunk.rejected, (state, action) => {
      state.inProgress = false;
      state.error = action.payload;
    });

    // to get the profile picture
    builder.addCase(uploadsProfilePictureThunk.pending, (state, action) => {
      state.inProgress = true;
      state.error = null;
    });
    builder.addCase(uploadsProfilePictureThunk.fulfilled, (state, action) => {
      state.inProgress = false;
    });
    builder.addCase(uploadsProfilePictureThunk.rejected, (state, action) => {
      state.inProgress = false;
      state.error = action.payload;
    });

    // getMe
    builder.addCase(getMeThunk.pending, (state, action) => {
      state.inProgress = true;
      state.error = null;
    });
    builder.addCase(getMeThunk.fulfilled, (state, action) => {
      state.inProgress = false;
      const user = action.payload.data.user;
      state.user = user;
    });
    builder.addCase(getMeThunk.rejected, (state, action) => {
      state.inProgress = false;
      state.error = action.payload;
    });

    builder.addCase(getAddressSuggestionsThunk.pending, (state, action) => {
      state.inProgress = true;
      state.error = null;
    });
    builder.addCase(getAddressSuggestionsThunk.fulfilled, (state, action) => {
      state.inProgress = false;
      state.error = action.payload;
      // state.addressSuggestions = action.payload.data //Array.isArray(action.payload.data) ? action.payload.data : [];
      // const responseData = action.payload;
      // state.addressSuggestions = responseData.data;
    });
    builder.addCase(getAddressSuggestionsThunk.rejected, (state, action) => {
      state.inProgress = false;
      state.error = action.payload;
    });

    builder.addCase(getNearbySuburbsThunk.pending, (state, action) => {
      state.inProgress = true;
      state.error = null;
    });
    builder.addCase(getNearbySuburbsThunk.fulfilled, (state, action) => {
      state.inProgress = false;
      state.nearbySuburbs = action.payload.suburbs;
    });
    builder.addCase(getNearbySuburbsThunk.rejected, (state, action) => {
      state.inProgress = false;
      state.error = action.payload;
    });

    builder.addCase(
      getJobDetailsBasedOnAddressThunk.pending,
      (state, action) => {
        state.inProgress = true;
        state.error = null;
      }
    );
    builder.addCase(
      getJobDetailsBasedOnAddressThunk.fulfilled,
      (state, action) => {
        state.inProgress = false;
      }
    );
    builder.addCase(
      getJobDetailsBasedOnAddressThunk.rejected,
      (state, action) => {
        state.inProgress = false;
        state.error = action.payload;
      }
    );
  },
});

export const {
  setUserAction,
  appLogOutAction,
  setProfilePictureAction,
  setTokenAction, 
  resetAuthErrorAction,
  setShowSignUpDetails,
  setPositionAction,
  resetAddressSuggestionsListAction,
  setShowNotVerifiedErrorAction,
  setSubmitStageAction
} = usersSlice.actions

export default usersSlice.reducer
